import React, { useRef, useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import InputPage from './pages/InputPage';
import WorkingPage from './pages/WorkingPage';
import ResultPage from './pages/ResultPage';

const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const VideoBackground = styled.video`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
  transition: opacity 0.2s linear;
  opacity: ${props => props.opacity};
`;

const AuthorSignature = styled.div`
  position: fixed;
  bottom: 10px;
  right: 15px;
  font-size: 14px;
  color: var(--gold-color);
  opacity: 0.7;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  z-index: 50;
  font-family: var(--main-font);
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
`;

// 调试信息组件，仅在开发环境显示
const DebugInfo = styled.div`
  position: fixed;
  top: 10px;
  left: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  border-radius: 5px;
  font-family: monospace;
  font-size: 12px;
  z-index: 1000;
  max-width: 300px;
  display: ${process.env.NODE_ENV === 'development' ? 'block' : 'none'};
`;

// 视频的精确时长（秒）- 根据用户反馈调整
const EXACT_VIDEO_DURATION = 4.620771; // 调整为精确的4.621秒

// 不透明度范围
const MIN_OPACITY = 0.08; // 最暗状态为3%
const MAX_OPACITY = 0.62; // 最亮状态为100%

function App() {
  const videoRef = useRef(null);
  const opacityRef = useRef(MIN_OPACITY); // 使用ref存储当前不透明度，避免状态更新延迟
  const [videoOpacity, setVideoOpacity] = useState(MIN_OPACITY); // 初始为最暗状态
  const [debugInfo, setDebugInfo] = useState({
    actualDuration: 0,
    usedDuration: EXACT_VIDEO_DURATION,
    currentTime: 0,
    opacity: MIN_OPACITY
  });
  
  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (videoElement) {
      // 设置循环属性
      videoElement.loop = true;
      
      // 确保获取准确的视频时长
      const handleMetadataLoaded = () => {
        const duration = videoElement.duration;
        console.log("视频元数据加载完成，时长:", duration);
        setDebugInfo(prev => ({ ...prev, actualDuration: duration }));
      };
      
      // 监听元数据加载事件
      videoElement.addEventListener('loadedmetadata', handleMetadataLoaded);
      
      // 视频可以播放时开始同步闪烁效果
      videoElement.addEventListener('canplay', () => {
        console.log("视频可以播放，当前时长:", videoElement.duration);
        
        // 创建一个平滑的不透明度变化函数
        const calculateSmoothOpacity = (time, duration) => {
          // 使用正弦函数创建平滑的波动
          // 将时间标准化到0-1范围
          const normalizedTime = time / duration;
          
          // 使用正弦函数，创建0到1再到0的平滑波形
          // 这样在视频开始和结束时都是平滑过渡
          // 使用(1 - cos(x))/2公式，这会产生0到1的平滑波形
          const value = (1 - Math.cos(normalizedTime * 2 * Math.PI)) / 2;
          
          // 将值(0到1)映射到不透明度(MIN_OPACITY到MAX_OPACITY)
          return MIN_OPACITY + (value * (MAX_OPACITY - MIN_OPACITY));
        };
        
        // 开始监听视频时间更新
        const handleTimeUpdate = () => {
          // 获取当前精确的播放时间
          const currentTime = videoElement.currentTime;
          
          // 计算平滑的不透明度
          const opacity = calculateSmoothOpacity(currentTime, EXACT_VIDEO_DURATION);
          
          // 更新不透明度引用
          opacityRef.current = opacity;
          
          // 设置不透明度状态
          setVideoOpacity(opacity);
          
          // 更新调试信息
          setDebugInfo({
            actualDuration: videoElement.duration.toFixed(3),
            usedDuration: EXACT_VIDEO_DURATION.toFixed(3),
            currentTime: currentTime.toFixed(3),
            normalizedTime: (currentTime / EXACT_VIDEO_DURATION).toFixed(3),
            value: ((1 - Math.cos((currentTime / EXACT_VIDEO_DURATION) * 2 * Math.PI)) / 2).toFixed(3),
            opacity: opacity.toFixed(3),
            minOpacity: MIN_OPACITY,
            maxOpacity: MAX_OPACITY
          });
        };
        
        // 每隔一小段时间更新一次不透明度，使过渡更平滑
        const opacityInterval = setInterval(handleTimeUpdate, 16); // 约60fps的更新频率
        
        // 清理函数
        return () => {
          clearInterval(opacityInterval);
        };
      });
      
      // 处理视频结束事件（作为备份）
      videoElement.addEventListener('ended', () => {
        if (!videoElement.loop) {
          videoElement.currentTime = 0;
          videoElement.play()
            .catch(error => {
              console.error("视频播放失败:", error);
            });
        }
      });
      
      // 组件卸载时清理
      return () => {
        if (videoElement) {
          videoElement.removeEventListener('loadedmetadata', handleMetadataLoaded);
          videoElement.removeEventListener('canplay', () => {});
          videoElement.removeEventListener('ended', () => {});
        }
      };
    }
  }, []);
  
  return (
    <Router>
      <AppContainer>
        <VideoBackground 
          ref={videoRef} 
          autoPlay 
          loop 
          muted 
          playsInline
          opacity={videoOpacity}
        >
          <source src="./mp4/bg-normal.mp4" type="video/mp4" />
          您的浏览器不支持视频标签。
        </VideoBackground>
        
        <DebugInfo>
          <div>实际视频时长: {debugInfo.actualDuration}秒</div>
          <div>使用的时长: {debugInfo.usedDuration}秒</div>
          <div>当前时间: {debugInfo.currentTime}秒</div>
          <div>归一化时间: {debugInfo.normalizedTime}</div>
          <div>波形值: {debugInfo.value}</div>
          <div>不透明度: {debugInfo.opacity}</div>
          <div>不透明度范围: {debugInfo.minOpacity} - {debugInfo.maxOpacity}</div>
        </DebugInfo>
        
        <Routes>
          <Route path="/" element={<InputPage />} />
          <Route path="/working" element={<WorkingPage />} />
          <Route path="/result" element={<ResultPage />} />
        </Routes>
        
        <AuthorSignature>by @刘小排</AuthorSignature>
      </AppContainer>
    </Router>
  );
}

export default App; 